import React, { useMemo, useState } from "react";
import {
  Modal,
  Table,
  TableColumnsType,
  Typography,
  Descriptions,
  Divider,
  Input,
} from "antd";
import {
  ICreatePartNumberTransfer,
  IPartNumberDtoTransfer,
  IWarehouseLocation,
} from "../../../models/Warehouse";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

import { notify } from "../../../utils/toast";
import { useTransferWarehouse } from "../../../query/WarehouseQuery";

interface ModalPreviewTransferProps {
  open: boolean;
  selectedItems: Array<IPartNumberDtoTransfer>;
  targetWarehouseId: number;
  targetLocationId: number;
  onOk: () => void;
  onCancel: () => void;
}

const ModalPreviewTransfer: React.FC<ModalPreviewTransferProps> = ({
  open,
  selectedItems,
  onOk,
  onCancel,
  targetLocationId,
  targetWarehouseId,
}) => {
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations,
  ) as IWarehouseLocation[];
  const targetWarehouse = locations.find(
    (item) => item.id === Number(targetWarehouseId),
  );
  const targetLocation = targetWarehouse?.warehouseLocations.find(
    (item) => item.id === Number(targetLocationId),
  );

  const [note, setNote] = useState<string>("");

  const totalQuantity = useMemo(
    () => selectedItems.reduce((sum, it) => sum + (it.exportQuantity || 0), 0),
    [selectedItems],
  );

  const totalReportSA = useMemo(
    () =>
      selectedItems.reduce((sum, it) => sum + (it.reportSAQuantity || 0), 0),
    [selectedItems],
  );
  const createTransferWarehouse = useTransferWarehouse();

  const columns: TableColumnsType<IPartNumberDtoTransfer> = [
    {
      title: "STT",
      dataIndex: "index",
      align: "center",
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      align: "center",
      width: 150,
    },
    {
      title: "AWB",
      dataIndex: "awb",
      align: "center",
      width: 120,
    },
    {
      title: "Dự án",
      dataIndex: "project",
      align: "center",
      width: 100,
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      align: "center",
      width: 120,
    },
    {
      title: "Kho hiện tại",
      dataIndex: ["location", "warehouse", "warehouseCode"],
      align: "center",
      width: 100,
    },
    {
      title: "Vị trí hiện tại",
      dataIndex: ["location", "code"],
      align: "center",
      width: 120,
    },
    {
      title: "Tồn kho",
      dataIndex: "stock",
      align: "center",
      width: 100,
      render: (v: number) => (
        <span className="font-medium text-gray-600">{v}</span>
      ),
    },
    {
      title: "SL SA báo",
      dataIndex: "reportSAQuantity",
      align: "center",
      width: 100,
      render: (v: number) => (
        <span className="font-medium text-orange-600">{v}</span>
      ),
    },
    {
      title: "SL xuất",
      dataIndex: "exportQuantity",
      align: "center",
      width: 100,
      render: (v: number) => (
        <span className="font-medium text-blue-600">{v}</span>
      ),
    },
    {
      title: "Đơn vị",
      dataIndex: "unit",
      align: "center",
      width: 80,
      render: (value: string) =>
        value === "pcs" || value === "PCS"
          ? "Cái"
          : value === "set"
            ? "Bộ"
            : value,
    },
  ];

  const handleConfirm = async () => {
    const invalidItems = selectedItems.filter(
      (it) =>
        !it.exportQuantity ||
        it.exportQuantity <= 0 ||
        !it.reportSAQuantity ||
        it.reportSAQuantity <= 0,
    );

    if (invalidItems.length > 0) {
      notify.error(
        "Một số mã hàng chưa nhập đầy đủ số lượng. Vui lòng kiểm tra lại.",
      );
      return;
    }

    const overStockItems = selectedItems.filter(
      (it) => it.exportQuantity > it.stock,
    );

    if (overStockItems.length > 0) {
      notify.error(
        "Số lượng xuất không được vượt quá tồn kho. Vui lòng kiểm tra lại.",
      );
      return;
    }
    const data: ICreatePartNumberTransfer = {
      transferItems: selectedItems.map((item) => ({
        id: item.id,
        transferQuantity: item.exportQuantity,
        reportSAQuantity: item.reportSAQuantity,
      })),
      note: note,
      locationId: targetLocationId,
    };

    try {
      await createTransferWarehouse.mutateAsync(data);
      notify.success("Điều chuyển kho thành công");
      onOk();
    } catch (error) {
      notify.error("Có lỗi xảy ra. Vui lòng thử lại.");
    }
  };

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center gap-2">
          <Typography.Title
            level={4}
            style={{
              margin: 0,
              fontFamily: "Chakra Petch",
              textTransform: "uppercase",
            }}
          >
            Xác nhận điều chuyển
          </Typography.Title>
          <Typography.Text className="text-gray-600">
            (Kiểm tra kỹ trước khi xác nhận)
          </Typography.Text>
        </div>
      }
      width="80%"
      onOk={handleConfirm}
      onCancel={onCancel}
      okText="Xác nhận điều chuyển"
      cancelText="Đóng"
      centered
      confirmLoading={createTransferWarehouse.isPending}
    >
      <div className="space-y-6">
        <Descriptions bordered size="small">
          <Descriptions.Item label="Ngày" span={3}>
            {new Date().toLocaleString("vi-VN", { dateStyle: "full" })}
          </Descriptions.Item>
          <Descriptions.Item label="Kho xuất" span={2}>
            {targetWarehouse?.warehouseCode}
          </Descriptions.Item>
          <Descriptions.Item label="Vị trí xuất" span={2}>
            {targetLocation?.code}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">Chi tiết chuyển kho xuất</Divider>

        <Table
          size="small"
          pagination={false}
          columns={columns}
          dataSource={selectedItems}
          rowKey="id"
          bordered
          scroll={{ x: 1200 }}
          style={{
            borderRadius: 8,
            overflow: "hidden",
          }}
          className="table-bold-border"
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row style={{ fontWeight: "bold" }}>
                <Table.Summary.Cell index={0} colSpan={8} align="right">
                  <strong>Tổng cộng:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="center">
                  <span className="font-medium text-orange-600">
                    {totalReportSA}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="center">
                  <span className="font-medium text-blue-600">
                    {totalQuantity}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ghi chú
            </label>
            <Input.TextArea
              rows={3}
              placeholder="Ghi chú (nếu có)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalPreviewTransfer;
